import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, Camera, X, Loader2, FileText, ChevronRight, ChevronUp, ChevronDown, Plus, Power, Pencil, Copy, Download, FileSpreadsheet, Thermometer, ClipboardList, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { LocationSelect, useLocations } from "@/components/LocationSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PhotoCapture } from "@/components/PhotoCapture";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, fileUrl, API_BASE, getToken } from "@/lib/api";
import { EvidenceLightbox } from "@/components/EvidenceLightbox";
import { fmtDateTime } from "@/lib/format";

export default function Tareas({ isAdmin = false }) {
  const { activeLocation } = useAuth();
  if (!isAdmin) return <TareasHub locationId={activeLocation} />;
  return (
    <div className="">
      <PageHeader title="Tareas" subtitle="Cumplimiento operativo diario" right={<LocationSelect />} />
      <Tabs defaultValue="hoy">
        <TabsList className="mb-4">
          <TabsTrigger value="hoy" data-testid="tab-tareas-hoy">Hoy</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-tareas-historial">Historial</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-tareas-config">Configuración</TabsTrigger>
        </TabsList>
        <TabsContent value="hoy"><Hoy locationId={activeLocation} /></TabsContent>
        <TabsContent value="historial"><Historial isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="config"><Config /></TabsContent>
      </Tabs>
    </div>
  );
}

function TareasHub({ locationId }) {
  const nav = useNavigate();
  const [stats, setStats] = useState({ planillas: null, temperaturas: null });
  useEffect(() => {
    if (!locationId) { setStats({ planillas: { total: 0, done: 0 }, temperaturas: { total: 0, done: 0 } }); return; }
    setStats({ planillas: null, temperaturas: null });
    api.get(`/planillas/today?location_id=${locationId}`)
      .then(({ data }) => setStats((s) => ({ ...s, planillas: { total: data.length, done: data.filter((it) => ["COMPLETED", "OBSERVATION", "INCIDENT"].includes(it.status)).length, late: data.filter((it) => it.status === "LATE").length } })))
      .catch(() => setStats((s) => ({ ...s, planillas: { total: 0, done: 0, late: 0 } })));
    api.get(`/controls/today?location_id=${locationId}`)
      .then(({ data }) => setStats((s) => ({ ...s, temperaturas: { total: data.length, done: data.filter((it) => ["COMPLETED", "INCIDENT"].includes(it.status)).length, late: data.filter((it) => it.status === "LATE").length } })))
      .catch(() => setStats((s) => ({ ...s, temperaturas: { total: 0, done: 0, late: 0 } })));
  }, [locationId]);
  return (
    <div className="px-4 pt-5">
      <PageHeader title="Tareas" subtitle="¿Qué quieres controlar?" right={<LocationSelect />} />
      <div className="mt-2 space-y-4">
        <HubCard
          testid="hub-planillas"
          icon={ClipboardList}
          title="Control de planillas"
          text="Revisa y completa las planillas pendientes del turno."
          stats={stats.planillas}
          doneVerb="completadas"
          emptyLabel="Sin planillas programadas"
          cta="Ver planillas"
          onClick={() => nav("/app/planillas")}
        />
        <HubCard
          testid="hub-temperaturas"
          icon={Thermometer}
          title="Control de temperaturas"
          text="Registra la temperatura de los equipos del local."
          stats={stats.temperaturas}
          doneVerb="registradas"
          emptyLabel="Sin controles programados"
          cta="Ver equipos"
          onClick={() => nav("/app/controles")}
        />
      </div>
    </div>
  );
}

function HubCard({ testid, icon: Icon, title, text, stats, doneVerb, emptyLabel, cta, onClick }) {
  const loading = stats == null;
  const total = stats?.total || 0;
  const done = stats?.done || 0;
  const late = stats?.late || 0;
  const pending = Math.max(0, total - done);
  const complete = total > 0 && done >= total;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const barColor = complete ? "bg-emerald-500" : late > 0 ? "bg-amber-500" : "bg-[#2563EB]";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group block w-full rounded-3xl border border-[#E2E8F0] bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#2563EB]/40 hover:shadow-lg hover:shadow-[#2563EB]/5 active:translate-y-0"
    >
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#EFF4FF] text-[#2563EB]">
          <Icon className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-semibold text-[#153D35]">{title}</h2>
          <p className="mt-1 text-sm text-[#68756F]">{text}</p>
          {!loading && (
            <div className="mt-3">
              {total === 0 ? (
                <span data-testid={`${testid}-empty`} className="inline-flex items-center rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-400">
                  {emptyLabel}
                </span>
              ) : (
                <>
                  <p data-testid={`${testid}-progress-label`} className={`text-sm font-semibold ${complete ? "text-emerald-600" : "text-[#153D35]"}`}>
                    {complete ? "Completado" : `${done} de ${total} ${doneVerb}`}
                  </p>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#EEF2F6]">
                    <div
                      data-testid={`${testid}-progress-bar`}
                      className={`h-full rounded-full transition-all ${barColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {!complete && (
                    <div className="mt-1.5 flex items-center gap-2">
                      {late > 0 && (
                        <span data-testid={`${testid}-late`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                          <AlertTriangle className="h-3.5 w-3.5" /> {late} atrasada{late === 1 ? "" : "s"}
                        </span>
                      )}
                      <span data-testid={`${testid}-pending`} className="text-xs text-[#94A3B8]">
                        {pending} pendiente{pending === 1 ? "" : "s"}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-[#F1F5F9] pt-3">
        <span className="text-sm font-semibold text-[#2563EB]">{cta}</span>
        <ChevronRight className="h-5 w-5 text-[#2563EB] transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

function TaskCard({ it, onDo }) {
  const done = ["COMPLETED", "NOT_REQUIRED"].includes(it.status);
  return (
    <div data-testid={`task-${it.id}`} className="flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-[#153D35]">{it.name}</p>
        <p className="truncate text-xs text-[#68756F]">{it.category}{it.completed_at ? ` · ${it.user_name || ""}` : ""}</p>
        <div className="mt-2"><StatusPill status={it.status} /></div>
      </div>
      <Button data-testid={`do-task-${it.id}`} onClick={() => onDo(it)} className="h-11 shrink-0 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
        {done ? "Ver" : "Realizar"}
      </Button>
    </div>
  );
}

function Hoy({ locationId }) {
  const [items, setItems] = useState([]);
  const [defs, setDefs] = useState([]);
  const [exec, setExec] = useState(null);
  const load = () => {
    if (!locationId) { setItems([]); return; }
    api.get(`/tasks/today?location_id=${locationId}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get(`/tasks/definitions`).then(({ data }) => setDefs(data.filter((d) => d.frequency === "ON_DEMAND"))).catch(() => {});
  };
  useEffect(load, [locationId]);
  const startOnDemand = async (d) => {
    try { const { data } = await api.post(`/tasks/instances/start`, { task_definition_id: d.id, location_id: locationId }); setExec(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!locationId) return <EmptyState icon={ListChecks} title="Selecciona un local" />;
  const byShift = {};
  items.forEach((i) => { (byShift[i.shift_id || "otros"] ||= []).push(i); });
  return (
    <div className="space-y-6">
      {items.length === 0 && defs.length === 0 ? <EmptyState icon={ListChecks} title="Sin tareas programadas" /> : null}
      {Object.entries(byShift).map(([sid, list]) => (
        <div key={sid}>
          <div className="space-y-3">{list.map((it) => <TaskCard key={it.id} it={it} onDo={setExec} />)}</div>
        </div>
      ))}
      {defs.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Según ocurrencia</p>
          <div className="space-y-3">
            {defs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4">
                <div className="min-w-0 flex-1"><p className="truncate font-medium text-[#153D35]">{d.name}</p><p className="text-xs text-[#68756F]">{d.category} · se inicia cuando corresponde</p></div>
                <Button data-testid={`start-ondemand-${d.id}`} variant="outline" onClick={() => startOnDemand(d)} className="h-11 shrink-0 rounded-xl"><Plus className="mr-1 h-4 w-4" /> Iniciar</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <ExecuteModal task={exec} onClose={() => setExec(null)} onDone={() => { setExec(null); load(); }} />
    </div>
  );
}

function ExecuteModal({ task, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  const [lb, setLb] = useState(null);
  useEffect(() => { setPhotos([]); setObs(""); setLb(null); }, [task]);
  if (!task) return null;
  const readonly = ["COMPLETED", "NOT_REQUIRED"].includes(task.status);
  const evidenceMode = task.evidence_mode || (task.photo_required ? "REQUIRED" : "OPTIONAL");
  const obsMode = task.observation_mode || (task.observation_allowed === false ? "NONE" : "OPTIONAL");
  const minPhotos = task.min_photos || 1;
  const allowGallery = !!task.allow_gallery;
  const photoInstruction = task.photo_instruction || "Toma una foto de la planilla completada.";
  const maxPhotos = Math.max(minPhotos, 3);
  const needPhotos = evidenceMode === "REQUIRED";
  const enoughPhotos = evidenceMode === "NONE" ? true : (needPhotos ? photos.length >= minPhotos : true);
  const obsOk = obsMode !== "REQUIRED" || obs.trim().length > 0;
  const canSubmit = enoughPhotos && obsOk;
  const submit = async () => {
    if (!canSubmit) {
      if (!enoughPhotos) toast.error(`Debes adjuntar al menos ${minPhotos} foto(s) de la planilla`);
      else toast.error("Esta tarea requiere una observación");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tasks/instances/${task.id}/complete`, { photos_base64: photos, observation: obs || null });
      toast.success("Tarea realizada"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md" aria-describedby={undefined}
        onEscapeKeyDown={(e) => { if (lb != null) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (lb != null) e.preventDefault(); }}
        onInteractOutside={(e) => { if (lb != null) e.preventDefault(); }}>
        <DialogHeader><DialogTitle className="font-display">{task.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-[#F8FAFC] p-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-slate-400">Estado</span><StatusPill status={task.status} /></div>
            {task.instructions && <p className="mt-2 text-[#1e3a8a]">{task.instructions}</p>}
          </div>
          {readonly ? (
            <div className="space-y-3">
              <p className="text-sm text-[#68756F]">Realizada por <b>{task.user_name}</b> · {task.completed_at ? fmtDateTime(task.completed_at) : ""}</p>
              {(task.evidence || []).length > 0 && <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Foto de respaldo</p>}
              <div className="grid grid-cols-2 gap-2">
                {(task.evidence || []).map((ev, i) => (
                  <button key={i} type="button" data-testid={`task-evidence-${i}`} onClick={() => setLb(i)} className="block">
                    <img src={fileUrl(ev.path)} alt="" className="aspect-square w-full rounded-xl object-cover" />
                  </button>
                ))}
              </div>
              {task.observation && <p className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">Obs: {task.observation}</p>}
            </div>
          ) : (
            <>
              {evidenceMode !== "NONE" && (
                <div className="space-y-2">
                  {photos.length > 0 && (
                    <>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Foto de respaldo{needPhotos ? ` (${photos.length}/${minPhotos})` : ""}</p>
                      <div className="grid grid-cols-3 gap-2">
                        {photos.map((p, i) => (
                          <div key={i} className="relative">
                            <button type="button" onClick={() => window.open(p, "_blank")} className="block w-full"><img src={p} alt="" className="aspect-square w-full rounded-lg object-cover" /></button>
                            <button data-testid={`task-photo-remove-${i}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full bg-white shadow"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {photos.length < maxPhotos && (
                    <PhotoCapture
                      onCapture={(d) => setPhotos((x) => [...x, d])}
                      testidPrefix="task-photo"
                      instruction={photoInstruction}
                      ctaLabel="Tomar foto de la planilla"
                      allowGallery={allowGallery}
                      compact
                      confirmStep
                    />
                  )}
                  {needPhotos && <p className="text-xs text-slate-400">Mínimo {minPhotos} foto(s) para completar.</p>}
                </div>
              )}
              {obsMode !== "NONE" && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-[#153D35]">Observación{obsMode === "REQUIRED" && <span className="text-red-500"> *</span>}</p>
                  <Textarea data-testid="task-observation" value={obs} onChange={(e) => setObs(e.target.value)} placeholder={obsMode === "REQUIRED" ? "Observación obligatoria" : "Observación (opcional)"} />
                </div>
              )}
            </>
          )}
        </div>
        {!readonly && <DialogFooter><Button data-testid="task-complete-btn" disabled={saving || !canSubmit} onClick={submit} className="h-12 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Marcar como realizada"}</Button></DialogFooter>}
      </DialogContent>
      {lb != null && (
        <EvidenceLightbox
          images={(task.evidence || []).map((ev) => ({ url: fileUrl(ev.path) }))}
          index={lb}
          meta={{ name: task.name, location: task.location_name,
                  datetime: task.completed_at ? fmtDateTime(task.completed_at) : task.date,
                  user: task.user_name }}
          onClose={() => setLb(null)}
          onIndex={setLb}
        />
      )}
    </Dialog>
  );
}

function Historial({ isAdmin }) {
  const { locations } = useLocations();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ location_id: "", status: "", date_from: "", date_to: "" });
  const [exec, setExec] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const load = () => { const p = new URLSearchParams({ limit: "200" }); Object.entries(f).forEach(([k, v]) => v && p.append(k, v)); api.get(`/tasks/instances?${p}`).then(({ data }) => setItems(data)).catch(() => setItems([])); };
  useEffect(load, [f]); // eslint-disable-line
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={f.location_id || "all"} onValueChange={(v) => setF({ ...f, location_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="task-hist-loc" className="h-10 w-40 bg-white"><SelectValue placeholder="Local" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los locales</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="task-hist-status" className="h-10 w-40 bg-white"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="COMPLETED">Completada</SelectItem><SelectItem value="PENDING">Pendiente</SelectItem><SelectItem value="LATE">Atrasada</SelectItem><SelectItem value="NOT_REQUIRED">No aplica</SelectItem></SelectContent>
        </Select>
        <Input data-testid="task-hist-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-36" />
        <Input data-testid="task-hist-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-36" />
        {isAdmin && <Button data-testid="task-export-btn" onClick={() => setExportOpen(true)} className="ml-auto h-10 rounded-xl bg-[#153D35] hover:bg-[#0f2c27]"><Download className="mr-2 h-4 w-4" /> Exportar</Button>}
      </div>
      {items.length === 0 ? <EmptyState icon={FileText} title="Sin registros" /> : (
        <div className="space-y-3">
          {items.map((s) => (
            <button key={s.id} data-testid={`task-hist-${s.id}`} onClick={() => setExec(s)} className="flex w-full items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4 text-left hover:shadow-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-[#153D35]">{s.name}</p>
                <p className="truncate text-xs text-[#68756F]">{s.date} · {s.shift_name || "—"} · {s.location_name}{isAdmin && s.user_name ? ` · ${s.user_name}` : ""}</p>
                <div className="mt-2"><StatusPill status={s.status} /></div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300" />
            </button>
          ))}
        </div>
      )}
      <ExecuteModal task={exec} onClose={() => setExec(null)} onDone={() => { setExec(null); load(); }} />
      {isAdmin && <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} locations={locations} initial={f} />}
    </div>
  );
}

function ExportDialog({ open, onClose, locations, initial }) {
  const [fmt, setFmt] = useState("pdf");
  const [f, setF] = useState({ location_id: "", shift_id: "", status: "", category: "", user_id: "", date_from: "", date_to: "", include_photos: false });
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF((x) => ({ ...x, location_id: initial?.location_id || "", status: initial?.status || "", date_from: initial?.date_from || "", date_to: initial?.date_to || "" }));
    api.get("/tasks/shifts").then(({ data }) => setShifts(data)).catch(() => {});
    api.get("/tasks/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, [open]); // eslint-disable-line
  const qs = () => { const p = new URLSearchParams(); Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) p.append(k, v); }); return p; };
  useEffect(() => {
    if (!open) return;
    setLoadingPrev(true);
    const p = qs();
    api.get(`/tasks/report/preview?${p}`).then(({ data }) => setPreview(data)).catch(() => setPreview(null)).finally(() => setLoadingPrev(false));
  }, [open, f.location_id, f.shift_id, f.status, f.category, f.user_id, f.date_from, f.date_to]); // eslint-disable-line
  const photosAllowed = preview ? preview.photos_allowed : true;
  useEffect(() => { if (!photosAllowed && f.include_photos) setF((x) => ({ ...x, include_photos: false })); }, [photosAllowed]); // eslint-disable-line
  const generate = () => {
    const p = qs();
    p.append("auth", getToken());
    if (fmt === "pdf" && f.include_photos) p.set("include_photos", "true");
    window.open(`${API_BASE}/tasks/report/${fmt}?${p.toString()}`, "_blank");
  };
  const Stat = ({ label, value, tone }) => (
    <div className="rounded-xl bg-[#F8FAFC] p-3 text-center"><p className="text-xs text-[#68756F]">{label}</p><p className={`text-xl font-bold ${tone || "text-[#153D35]"}`}>{value}</p></div>
  );
  const s = preview?.summary;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Exportar cumplimiento de Tareas</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div><p className="mb-1 text-xs text-slate-400">Formato</p>
              <Select value={fmt} onValueChange={setFmt}><SelectTrigger data-testid="export-format" className="h-10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="excel">Excel</SelectItem></SelectContent></Select>
            </div>
            <div><p className="mb-1 text-xs text-slate-400">Local</p>
              <Select value={f.location_id || "all"} onValueChange={(v) => setF({ ...f, location_id: v === "all" ? "" : v })}><SelectTrigger data-testid="export-location" className="h-10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos los locales</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><p className="mb-1 text-xs text-slate-400">Desde</p><Input data-testid="export-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10" /></div>
            <div><p className="mb-1 text-xs text-slate-400">Hasta</p><Input data-testid="export-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10" /></div>
            <div><p className="mb-1 text-xs text-slate-400">Turno</p>
              <Select value={f.shift_id || "all"} onValueChange={(v) => setF({ ...f, shift_id: v === "all" ? "" : v })}><SelectTrigger data-testid="export-shift" className="h-10"><SelectValue placeholder="Todos" /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{shifts.map((sh) => <SelectItem key={sh.id} value={sh.id}>{sh.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><p className="mb-1 text-xs text-slate-400">Estado</p>
              <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}><SelectTrigger data-testid="export-status" className="h-10"><SelectValue placeholder="Todos" /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="COMPLETED">Completada</SelectItem><SelectItem value="PENDING">Pendiente</SelectItem><SelectItem value="LATE">Atrasada</SelectItem><SelectItem value="NOT_REQUIRED">No aplica</SelectItem></SelectContent></Select>
            </div>
            <div><p className="mb-1 text-xs text-slate-400">Tipo de tarea</p>
              <Select value={f.category || "all"} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v })}><SelectTrigger data-testid="export-category" className="h-10"><SelectValue placeholder="Todas" /></SelectTrigger><SelectContent><SelectItem value="all">Todas</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><p className="mb-1 text-xs text-slate-400">Responsable</p>
              <Select value={f.user_id || "all"} onValueChange={(v) => setF({ ...f, user_id: v === "all" ? "" : v })}><SelectTrigger data-testid="export-user" className="h-10"><SelectValue placeholder="Todos" /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>

          {s && (
            <div className="grid grid-cols-5 gap-2" data-testid="export-preview">
              <Stat label="Esperadas" value={s.total} />
              <Stat label="Completas" value={s.done} tone="text-emerald-600" />
              <Stat label="Pend." value={s.pending} tone="text-amber-600" />
              <Stat label="Atras." value={s.late} tone="text-red-600" />
              <Stat label="Cumpl." value={`${s.compliance}%`} tone="text-[#2563EB]" />
            </div>
          )}
          {loadingPrev && <p className="text-center text-xs text-slate-400">Calculando…</p>}

          {fmt === "pdf" && (
            <div className="rounded-xl border border-[#E2E8F0] p-3">
              <label className={`flex items-center gap-2 text-sm ${photosAllowed ? "" : "opacity-50"}`}>
                <Checkbox data-testid="export-photos" checked={f.include_photos} disabled={!photosAllowed} onCheckedChange={(v) => setF({ ...f, include_photos: !!v })} />
                Incluir fotografías de evidencia
              </label>
              {preview && !photosAllowed && (
                <p data-testid="export-photos-warning" className="mt-2 text-xs text-red-600">Este reporte contiene más de {preview.photo_limit} evidencias ({preview.evidence_count}). Acota el rango de fechas/locales o genera el PDF sin fotografías.</p>
              )}
              {preview && photosAllowed && preview.evidence_count > 0 && (
                <p className="mt-2 text-xs text-slate-400">{preview.evidence_count} evidencia(s) disponibles para el anexo.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="export-generate-btn" onClick={generate} disabled={loadingPrev} className="h-11 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
            {fmt === "pdf" ? <FileText className="mr-2 h-4 w-4" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Generar reporte
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FREQ_LABEL = { PER_SHIFT: "Por turno", ONCE_DAILY: "Una vez al día", ON_DEMAND: "Según ocurrencia", SCHEDULED: "Programada" };
const EVIDENCE_LABEL = { REQUIRED: "Foto obligatoria", OPTIONAL: "Foto opcional", NONE: "Sin evidencia" };
const OBS_LABEL = { REQUIRED: "Obligatoria", OPTIONAL: "Opcional", NONE: "No disponible" };

function Config() {
  const { locations } = useLocations();
  const [defs, setDefs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => { api.get(`/tasks/definitions?include_inactive=true`).then(({ data }) => setDefs(data)).catch(() => {}); api.get(`/tasks/shifts`).then(({ data }) => setShifts(data)).catch(() => {}); };
  useEffect(load, []);
  const toggle = async (d) => { await api.post(`/tasks/definitions/${d.id}/toggle`); load(); };
  const duplicate = async (d) => { try { await api.post(`/tasks/definitions/${d.id}/duplicate`); toast.success("Tarea duplicada"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const move = async (idx, dir) => {
    const arr = [...defs]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setDefs(arr);
    try { await api.post(`/tasks/definitions/reorder`, { ordered_ids: arr.map((x) => x.id) }); } catch { load(); }
  };
  const EMPTY = { name: "", description: "", category: "Otro", frequency: "PER_SHIFT", instructions: "", location_ids: [], shift_ids: [], once_daily_shift_id: null, target_time: "", tolerance_minutes: 30, evidence_mode: "REQUIRED", min_photos: 1, allow_gallery: false, observation_mode: "OPTIONAL", photo_instruction: "", active: true };
  return (
    <div>
      <div className="mb-4 flex justify-end"><Button data-testid="new-task-btn" onClick={() => setEdit(EMPTY)} className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]"><Plus className="mr-2 h-4 w-4" /> Nueva tarea</Button></div>
      <div className="space-y-2">
        {defs.map((d, idx) => (
          <div key={d.id} data-testid={`taskdef-${d.id}`} className={`flex items-center gap-3 rounded-2xl border bg-white p-4 ${d.active ? "border-[#E2E8F0]" : "opacity-60"}`}>
            <div className="flex flex-col">
              <button data-testid={`move-up-${d.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-[#153D35] disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button data-testid={`move-down-${d.id}`} onClick={() => move(idx, 1)} disabled={idx === defs.length - 1} className="text-slate-400 hover:text-[#153D35] disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-[#153D35]">{d.name}{!d.active && <span className="ml-2 text-xs text-slate-400">(inactiva)</span>}</p>
              <p className="truncate text-xs text-[#68756F]">{d.category} · {FREQ_LABEL[d.frequency] || d.frequency} · {EVIDENCE_LABEL[d.evidence_mode || (d.photo_required ? "REQUIRED" : "OPTIONAL")]}</p>
            </div>
            <Button data-testid={`edit-task-${d.id}`} size="sm" variant="outline" onClick={() => setEdit(d)} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
            <Button data-testid={`duplicate-task-${d.id}`} size="sm" variant="ghost" onClick={() => duplicate(d)} className="rounded-lg text-slate-500"><Copy className="h-3.5 w-3.5" /></Button>
            <Button data-testid={`toggle-task-${d.id}`} size="sm" variant="ghost" onClick={() => toggle(d)} className="rounded-lg text-slate-500"><Power className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <DefEditor def={edit} shifts={shifts} locations={locations} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

function DefEditor({ def, shifts, locations, onClose, onSaved }) {
  const [d, setD] = useState(def);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  useEffect(() => { setD(def); setPreview(false); }, [def]);
  if (!def || !d) return null;
  const upd = (p) => setD((x) => ({ ...x, ...p }));
  const toggleShift = (sid) => upd({ shift_ids: d.shift_ids?.includes(sid) ? d.shift_ids.filter((x) => x !== sid) : [...(d.shift_ids || []), sid] });
  const hasEvidence = (d.evidence_mode || "REQUIRED") !== "NONE";
  const hasTime = !!d.target_time;
  const save = async () => {
    if (!d.name) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      const payload = { ...d, target_time: d.target_time || null, tolerance_minutes: Number(d.tolerance_minutes) || 30, min_photos: Number(d.min_photos) || 1 };
      if (d.id) await api.put(`/tasks/definitions/${d.id}`, payload); else await api.post(`/tasks/definitions`, payload);
      toast.success("Tarea guardada"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const locLabel = (d.location_ids || []).length === 0 ? "Todos los locales" : locations.filter((l) => d.location_ids.includes(l.id)).map((l) => l.name).join(", ");
  const shiftLabel = () => {
    if (d.frequency === "ONCE_DAILY") return (shifts.find((s) => s.id === d.once_daily_shift_id) || {}).name || "—";
    if (d.frequency === "PER_SHIFT") return (d.shift_ids || []).map((id) => (shifts.find((s) => s.id === id) || {}).name).filter(Boolean).join(", ") || "—";
    return "—";
  };
  return (
    <Dialog open={!!def} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{preview ? "Vista previa" : (d.id ? "Editar tarea" : "Nueva tarea")}</DialogTitle></DialogHeader>
        {preview ? (
          <div className="space-y-2 text-sm">
            {[["Nombre", d.name], ["Locales", locLabel], ["Turnos", shiftLabel()], ["Frecuencia", FREQ_LABEL[d.frequency]], ["Hora objetivo", hasTime ? `${d.target_time} (±${d.tolerance_minutes} min)` : "Sin hora límite"], ["Evidencia", EVIDENCE_LABEL[d.evidence_mode || "REQUIRED"]], ["Mínimo de fotos", hasEvidence && (d.evidence_mode === "REQUIRED") ? d.min_photos : "—"], ["Galería", hasEvidence ? (d.allow_gallery ? "Permitida" : "Solo cámara") : "—"], ["Observación", OBS_LABEL[d.observation_mode || "OPTIONAL"]]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-dashed border-[#E2E8F0] py-1.5"><span className="text-slate-400">{k}</span><span className="text-right font-medium text-[#153D35]">{v}</span></div>
            ))}
            <DialogFooter className="pt-3">
              <Button variant="outline" onClick={() => setPreview(false)} className="rounded-xl">Volver a editar</Button>
              <Button data-testid="save-task-btn" disabled={saving} onClick={save} className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar tarea"}</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-3">
          <Input data-testid="taskdef-name" value={d.name} onChange={(e) => upd({ name: e.target.value })} placeholder="Nombre de la tarea" className="h-11" />
          <Input value={d.description || ""} onChange={(e) => upd({ description: e.target.value })} placeholder="Descripción (opcional)" className="h-11" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={d.category} onValueChange={(v) => upd({ category: v })}><SelectTrigger data-testid="taskdef-category" className="h-11"><SelectValue /></SelectTrigger><SelectContent>{["Temperaturas", "Limpieza", "Personal", "Recepción", "Manipulación", "Saneamiento", "Otro"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <Select value={d.frequency} onValueChange={(v) => upd(v === "SCHEDULED" && !d.schedule ? { frequency: v, schedule: { type: "DATE", dates: [] } } : { frequency: v })}><SelectTrigger data-testid="taskdef-frequency" className="h-11"><SelectValue /></SelectTrigger><SelectContent>{["PER_SHIFT", "ONCE_DAILY", "ON_DEMAND", "SCHEDULED"].map((f) => <SelectItem key={f} value={f}>{FREQ_LABEL[f]}</SelectItem>)}</SelectContent></Select>
          </div>
          <Textarea value={d.instructions || ""} onChange={(e) => upd({ instructions: e.target.value })} placeholder="Instrucciones para el operario" />
          {["PER_SHIFT", "ONCE_DAILY"].includes(d.frequency) && (
            <div><p className="mb-1 text-sm text-[#153D35]">Turnos</p><div className="flex flex-wrap gap-3">{shifts.map((s) => <label key={s.id} className="flex items-center gap-1.5 text-sm"><Checkbox checked={d.shift_ids?.includes(s.id)} onCheckedChange={() => toggleShift(s.id)} /> {s.name}</label>)}</div></div>
          )}
          {d.frequency === "ONCE_DAILY" && (
            <Select value={d.once_daily_shift_id || ""} onValueChange={(v) => upd({ once_daily_shift_id: v })}><SelectTrigger className="h-11"><SelectValue placeholder="Turno preferente (opcional)" /></SelectTrigger><SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          )}
          {d.frequency === "SCHEDULED" && <ScheduleEditor schedule={d.schedule || { type: "DATE", dates: [] }} onChange={(schedule) => upd({ schedule })} />}
          <div><p className="mb-1 text-sm text-[#153D35]">Locales (vacío = todos)</p><div className="flex flex-wrap gap-3">{locations.map((l) => <label key={l.id} className="flex items-center gap-1.5 text-sm"><Checkbox checked={d.location_ids?.includes(l.id)} onCheckedChange={() => upd({ location_ids: d.location_ids?.includes(l.id) ? d.location_ids.filter((x) => x !== l.id) : [...(d.location_ids || []), l.id] })} /> {l.name}</label>)}</div></div>

          <div className="rounded-xl border border-[#E2E8F0] p-3 space-y-3">
            <p className="text-sm font-semibold text-[#153D35]">Evidencia requerida</p>
            <Select value={d.evidence_mode || "REQUIRED"} onValueChange={(v) => upd({ evidence_mode: v })}><SelectTrigger data-testid="taskdef-evidence" className="h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="REQUIRED">Foto obligatoria</SelectItem><SelectItem value="OPTIONAL">Foto opcional</SelectItem><SelectItem value="NONE">Sin evidencia</SelectItem></SelectContent></Select>
            {hasEvidence && (
              <div className="grid grid-cols-2 gap-2">
                {d.evidence_mode !== "OPTIONAL" && (
                  <div><p className="mb-1 text-xs text-slate-400">Mínimo de fotos</p>
                    <Select value={String(d.min_photos || 1)} onValueChange={(v) => upd({ min_photos: Number(v) })}><SelectTrigger data-testid="taskdef-minphotos" className="h-11"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
                  </div>
                )}
                <label className="flex items-center gap-2 self-end pb-2 text-sm"><Checkbox data-testid="taskdef-gallery" checked={!!d.allow_gallery} onCheckedChange={(v) => upd({ allow_gallery: !!v })} /> Permitir galería</label>
              </div>
            )}
            {hasEvidence && <Input data-testid="taskdef-photo-instruction" value={d.photo_instruction || ""} onChange={(e) => upd({ photo_instruction: e.target.value })} placeholder="Instrucción para la fotografía (opcional)" className="h-11" />}
          </div>

          <div><p className="mb-1 text-sm text-[#153D35]">Observación</p>
            <Select value={d.observation_mode || "OPTIONAL"} onValueChange={(v) => upd({ observation_mode: v })}><SelectTrigger data-testid="taskdef-observation" className="h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="NONE">No disponible</SelectItem><SelectItem value="OPTIONAL">Opcional</SelectItem><SelectItem value="REQUIRED">Obligatoria</SelectItem></SelectContent></Select>
          </div>

          <label className="flex items-center gap-2 text-sm"><Checkbox checked={hasTime} onCheckedChange={(v) => upd({ target_time: v ? "09:00" : "" })} /> Definir hora objetivo</label>
          {hasTime && (
            <div className="grid grid-cols-2 gap-2">
              <div><p className="mb-1 text-xs text-slate-400">Hora objetivo</p><Input data-testid="taskdef-target-time" type="time" value={d.target_time || ""} onChange={(e) => upd({ target_time: e.target.value })} className="h-11" /></div>
              <div><p className="mb-1 text-xs text-slate-400">Tolerancia (min)</p><Input value={d.tolerance_minutes} onChange={(e) => upd({ tolerance_minutes: e.target.value })} className="h-11 tnum" /></div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={d.active !== false} onCheckedChange={(v) => upd({ active: !!v })} /> Activa</label>
        </div>
        <DialogFooter>
          <Button data-testid="preview-task-btn" variant="outline" onClick={() => { if (!d.name) { toast.error("Nombre requerido"); return; } setPreview(true); }} className="rounded-xl">Vista previa</Button>
          <Button data-testid="save-task-btn" disabled={saving} onClick={save} className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar tarea"}</Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SCHED_WEEKDAYS = [["Lun", 0], ["Mar", 1], ["Mié", 2], ["Jue", 3], ["Vie", 4], ["Sáb", 5], ["Dom", 6]];

function ScheduleEditor({ schedule, onChange }) {
  const s = schedule || { type: "DATE", dates: [] };
  const upd = (p) => onChange({ ...s, ...p });
  const [newDate, setNewDate] = useState("");
  const addDate = () => { if (newDate && !(s.dates || []).includes(newDate)) upd({ dates: [...(s.dates || []), newDate].sort() }); setNewDate(""); };
  const toggleWd = (wd) => upd({ weekdays: (s.weekdays || []).includes(wd) ? s.weekdays.filter((x) => x !== wd) : [...(s.weekdays || []), wd] });
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-3">
      <Select value={s.type || "DATE"} onValueChange={(v) => onChange({ type: v, dates: [], weekdays: [], interval_days: 7 })}>
        <SelectTrigger data-testid="sched-type" className="h-10 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="DATE">Fecha(s) específica(s)</SelectItem>
          <SelectItem value="INTERVAL">Cada N días</SelectItem>
          <SelectItem value="WEEKLY">Días de la semana</SelectItem>
        </SelectContent>
      </Select>
      {(s.type || "DATE") === "DATE" && (
        <div>
          <div className="flex gap-2">
            <Input data-testid="sched-date-input" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-10 bg-white" />
            <Button data-testid="sched-date-add" type="button" variant="outline" onClick={addDate} className="h-10 rounded-lg"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(s.dates || []).map((dt) => (
              <span key={dt} className="flex items-center gap-1 rounded-full bg-white border border-[#E2E8F0] px-2 py-1 text-xs">{dt}
                <button type="button" onClick={() => upd({ dates: s.dates.filter((x) => x !== dt) })}><X className="h-3 w-3" /></button></span>
            ))}
          </div>
        </div>
      )}
      {s.type === "INTERVAL" && (
        <div className="grid grid-cols-2 gap-2">
          <div><p className="mb-1 text-xs text-slate-400">Desde</p><Input data-testid="sched-start" type="date" value={s.start_date || ""} onChange={(e) => upd({ start_date: e.target.value })} className="h-10 bg-white" /></div>
          <div><p className="mb-1 text-xs text-slate-400">Cada (días)</p><Input data-testid="sched-interval" value={s.interval_days || ""} onChange={(e) => upd({ interval_days: Number(e.target.value) || 0 })} className="h-10 bg-white tnum" /></div>
        </div>
      )}
      {s.type === "WEEKLY" && (
        <div className="flex flex-wrap gap-2">
          {SCHED_WEEKDAYS.map(([lbl, wd]) => (
            <label key={wd} className="flex items-center gap-1.5 rounded-lg bg-white border border-[#E2E8F0] px-2 py-1 text-sm"><Checkbox checked={(s.weekdays || []).includes(wd)} onCheckedChange={() => toggleWd(wd)} /> {lbl}</label>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">Define una "Hora objetivo" arriba para calcular atrasos (LATE).</p>
    </div>
  );
}
